import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Smartphone, User, Bell, Shield, CreditCard, HelpCircle, Home, 
  RefreshCcw, ToggleLeft, Droplet, BarChart3, Umbrella, UserPlus,
  Languages
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/contexts/LanguageContext';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import useTranslation from '@/hooks/use-translation';
import { cn } from '@/lib/utils';

const Settings = () => {
  const { language, setLanguage, isRTL } = useLanguage();
  const { translate } = useTranslation();
  
  return (
    <div className={cn("max-w-5xl mx-auto space-y-6 p-6", isRTL && "rtl")}>
      <h1 className="text-3xl font-bold">
        {translate("settings.title")}
      </h1>
          
          <Tabs defaultValue="account" className="w-full">
            <div className="flex overflow-x-auto pb-4">
              <TabsList className="mb-6">
            <TabsTrigger value="account" className={cn("flex items-center", isRTL && "flex-row-reverse")}>
              <User className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {translate("settings.account")}
            </TabsTrigger>
            <TabsTrigger value="language" className={cn("flex items-center", isRTL && "flex-row-reverse")}>
              <Languages className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {translate("common.language")}
                </TabsTrigger>
            <TabsTrigger value="notifications" className={cn("flex items-center", isRTL && "flex-row-reverse")}>
              <Bell className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {translate("settings.notifications")}
                </TabsTrigger>
            <TabsTrigger value="devices" className={cn("flex items-center", isRTL && "flex-row-reverse")}>
              <Smartphone className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {translate("settings.devices")}
                </TabsTrigger>
            <TabsTrigger value="privacy" className={cn("flex items-center", isRTL && "flex-row-reverse")}>
              <Shield className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {translate("settings.privacy")}
                </TabsTrigger>
            <TabsTrigger value="billing" className={cn("flex items-center", isRTL && "flex-row-reverse")}>
              <CreditCard className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {translate("settings.billing")}
                </TabsTrigger>
            <TabsTrigger value="help" className={cn("flex items-center", isRTL && "flex-row-reverse")}>
              <HelpCircle className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
              {translate("settings.help")}
                </TabsTrigger>
              </TabsList>
            </div>
            
        <TabsContent value="language">
                <Card>
                  <CardHeader>
              <CardTitle>{translate("settings.languageSettings")}</CardTitle>
              <CardDescription>{translate("settings.chooseLanguage")}</CardDescription>
                  </CardHeader>
                  <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-md font-medium mb-3">{translate("settings.selectLanguage")}</h3>
                  <RadioGroup
                    value={language}
                    onValueChange={(value) => setLanguage(value as 'en' | 'fr' | 'ar')}
                    className="grid gap-4"
                  >
                    <div className={cn("flex items-center space-x-2", isRTL && "flex-row-reverse space-x-reverse")}>
                      <RadioGroupItem value="en" id="en" />
                      <Label htmlFor="en" className="flex items-center">
                        <span className={cn("ml-2", isRTL && "ml-0 mr-2")}>English</span>
                        {language === 'en' && (
                          <Badge className={cn("ml-2 bg-primary/10 text-primary border-primary/20", isRTL && "ml-0 mr-2")}>
                            {translate("settings.current")}
                          </Badge>
                        )}
                      </Label>
                      </div>
                    <div className={cn("flex items-center space-x-2", isRTL && "flex-row-reverse space-x-reverse")}>
                      <RadioGroupItem value="fr" id="fr" />
                      <Label htmlFor="fr" className="flex items-center">
                        <span className={cn("ml-2", isRTL && "ml-0 mr-2")}>Français</span>
                        {language === 'fr' && (
                          <Badge className={cn("ml-2 bg-primary/10 text-primary border-primary/20", isRTL && "ml-0 mr-2")}>
                            {translate("settings.current")}
                          </Badge>
                        )}
                      </Label>
                    </div>
                    <div className={cn("flex items-center space-x-2", isRTL && "flex-row-reverse space-x-reverse")}>
                      <RadioGroupItem value="ar" id="ar" />
                      <Label htmlFor="ar" className="flex items-center">
                        <span className={cn("ml-2", isRTL && "ml-0 mr-2")}>العربية</span>
                        {language === 'ar' && (
                          <Badge className={cn("ml-2 bg-primary/10 text-primary border-primary/20", isRTL && "ml-0 mr-2")}>
                            {translate("settings.current")}
                          </Badge>
                        )}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div>
                  <h3 className="text-md font-medium mb-3">{translate("settings.textDirection")}</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {translate("settings.textDirectionAuto")}
                  </p>
                  <div className={cn("flex items-center space-x-2", isRTL && "flex-row-reverse space-x-reverse")}>
                    <Badge variant="outline">
                      {isRTL ? translate("settings.rightToLeft") : translate("settings.leftToRight")}
                    </Badge>
                      </div>
                    </div>
                    </div>
                  </CardContent>
                </Card>
        </TabsContent>
                
        <TabsContent value="account">
          <div className="space-y-6">
                <Card>
                  <CardHeader>
                <CardTitle>{translate("settings.profileInformation")}</CardTitle>
                <CardDescription>{translate("settings.updateAccountDetails")}</CardDescription>
                  </CardHeader>
                  <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                    <Label htmlFor="name">{translate("common.name")}</Label>
                    <Input id="name" defaultValue="John Doe" />
                      </div>
                      <div className="space-y-2">
                    <Label htmlFor="email">{translate("common.email")}</Label>
                    <Input id="email" type="email" defaultValue="john.doe@example.com" />
                      </div>
                    </div>
                    
                <div className={cn("mt-6 flex justify-end", isRTL && "flex-row-reverse")}>
                  <Button variant="outline" className={cn("mr-2", isRTL && "ml-2 mr-0")}>
                    {translate("common.cancel")}
                  </Button>
                  <Button>{translate("common.save")}</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="notifications">
              <Card>
                <CardHeader>
              <CardTitle>{translate("settings.notifPrefs")}</CardTitle>
              <CardDescription>{translate("settings.manageNotifications")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                  <h3 className="text-md font-medium mb-3">{translate("settings.waterUsageAlerts")}</h3>
                      <div className="space-y-3">
                    <div className={cn("flex items-center justify-between", isRTL && "flex-row-reverse")}>
                          <div className="space-y-0.5">
                        <Label htmlFor="daily-alerts">{translate("settings.dailyUsageSummary")}</Label>
                        <p className="text-sm text-muted-foreground">{translate("settings.dailyUsageSummaryDesc")}</p>
                          </div>
                          <Switch id="daily-alerts" defaultChecked />
                        </div>
                        <Separator />
                        </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="devices">
              <Card>
                <CardHeader>
              <CardTitle>{translate("settings.connectedDevices")}</CardTitle>
              <CardDescription>{translate("settings.manageDevices")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                <div className={cn("flex items-center justify-between p-4 border rounded-lg", isRTL && "flex-row-reverse")}>
                  <div className={cn("flex items-center", isRTL && "flex-row-reverse")}>
                    <div className={cn("h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center", isRTL ? "ml-4" : "mr-4")}>
                          <Droplet className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                      <h3 className="font-medium">{translate("settings.mainWaterMeter")}</h3>
                      <p className="text-sm text-muted-foreground">
                        {translate("settings.connectedSince", { date: "Mar 15, 2023" })}
                      </p>
                    </div>
                        </div>
                  <div className={cn("flex items-center", isRTL && "flex-row-reverse")}>
                    <Badge className={cn("bg-green-500/10 text-green-600 border-green-500/20", isRTL ? "ml-4" : "mr-4")}>Online</Badge>
                        <Button variant="ghost" size="sm">
                          <RefreshCcw className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
        <TabsContent value="privacy">
              <Card>
                <CardHeader>
              <CardTitle>{translate("settings.privacySettings")}</CardTitle>
              <CardDescription>{translate("settings.privacy")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="space-y-3">
                  <div className={cn("flex items-center justify-between", isRTL && "flex-row-reverse")}>
                        <div className="space-y-0.5">
                          <Label htmlFor="data-collection">Data Collection</Label>
                          <p className="text-sm text-muted-foreground">Allow us to collect anonymous usage data to improve our service</p>
                        </div>
                        <Switch id="data-collection" defaultChecked />
                      </div>
                      <Separator />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
        <TabsContent value="billing">
              <Card>
                <CardHeader>
              <CardTitle>{translate("settings.billingInfo")}</CardTitle>
              <CardDescription>{translate("settings.billing")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="p-4 border rounded-lg bg-muted/50">
                  <div className={cn("flex items-center justify-between mb-4", isRTL && "flex-row-reverse")}>
                        <div>
                          <h3 className="font-medium">Current Plan: Basic</h3>
                          <p className="text-sm text-muted-foreground">Free access to essential water monitoring features</p>
                        </div>
                        <Button>Upgrade Plan</Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
        <TabsContent value="help">
              <Card>
                <CardHeader>
              <CardTitle>{translate("settings.help")}</CardTitle>
              <CardDescription>{translate("settings.faq")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2">{translate("settings.faq")}</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-medium">How accurate is the water usage tracking?</h4>
                          <p className="text-sm text-muted-foreground mt-1">AquaGenius provides up to 95% accuracy when properly connected to compatible smart meters and devices.</p>
                        </div>
                      </div>
                    </div>
                    
                <div className={cn("flex flex-col md:flex-row gap-4", isRTL && "flex-row-reverse")}>
                      <Button variant="outline" className="flex-1">
                    <HelpCircle className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
                        View Support Documentation
                      </Button>
                      <Button className="flex-1">
                        Contact Support
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
    </div>
  );
};

export default Settings;
